import Navbar from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Download, ArrowLeft, Maximize2, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const LOGO_SVG = '/downloads/t1-logo.svg';

// Distressed / textured spin-offs of the original — same identity, looser feel.
// Files were generated by backend/scripts/generate_tekki_variants.py (Pillow).
const VARIANTS = [
  // Classic — user-approved keepers
  { key: 'distressed', label: 'Distressed', sub: 'Eroded edges + scratch texture' },
  { key: 'grunge',     label: 'Grunge',     sub: 'Cracked-concrete bleed-through' },
  { key: 'halftone',   label: 'Halftone',   sub: 'Dotted screen-print look' },
  // Wild — kept / softened
  { key: 'glitch',     label: 'Glitch',     sub: 'RGB split + scanline tear' },
  { key: 'shatter',    label: 'Shatter',    sub: 'Lightly fractured + hairline cracks' },
  { key: 'xray',       label: 'X-Ray neon', sub: 'Negative-render rebuild glow' },
  // Hand-crafted — burnt / brush / torn direction
  { key: 'brush',      label: 'Brushstroke', sub: 'Wobbly painted edge + brush hairs' },
  { key: 'burnt',      label: 'Burnt',      sub: 'Charred frayed edge + ember specks' },
  { key: 'torn',       label: 'Torn paper', sub: 'Ripped deckled edge + drop shadow' },
  { key: 'stencil',    label: 'Stencil',    sub: 'Banksy overspray + paint drips' },
  { key: 'riso',       label: 'Riso print', sub: 'Misregistered teal/magenta + grain' },
  { key: 'patchwork',  label: 'Patchwork',  sub: 'Tile-shifted pieces + tape strips' },
];

const TekkiPage = () => {
  const navigate = useNavigate();

  const triggerDownload = (file = 't1-logo.svg') => {
    const a = document.createElement('a');
    a.href = `/downloads/${file}`;
    a.download = file;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="tekki-page">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 pt-24 pb-12">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4"
          data-testid="tekki-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Tekki</h1>
          <p className="text-muted-foreground mt-2">
            One logo. Any size. Razor-sharp forever.
          </p>
        </div>

        {/* ── Original (clean SVG) — unchanged hero ── */}
        <Card className="border-border/40 overflow-hidden mb-6">
          <div
            className="flex items-center justify-center aspect-square"
            style={{ backgroundColor: '#0a0a0a' }}
            data-testid="tekki-preview"
          >
            <img
              src={LOGO_SVG}
              alt="T1 logo"
              className="w-2/3 h-2/3 object-contain"
            />
          </div>
        </Card>

        <Button
          size="lg"
          className="w-full mb-2 h-14 text-base"
          onClick={() => triggerDownload('t1-logo.svg')}
          data-testid="tekki-download-svg"
        >
          <Download className="w-5 h-5 mr-2" />
          Download T1 logo (SVG)
        </Button>

        <button
          type="button"
          onClick={() => triggerDownload('t1-logo.png')}
          className="block mx-auto text-xs text-muted-foreground hover:text-foreground transition mb-8 underline underline-offset-4 decoration-dotted"
          data-testid="tekki-download-png"
        >
          Need a PNG instead? Download flat version (4500×4500)
        </button>

        {/* ── Funky variants ── */}
        <div className="mb-6 flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Funky variants</h2>
          <span className="text-xs text-muted-foreground">PNG · 2000×2000</span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Same logo, looser feel — for sleeve patches, sticker packs, social posts and the moments where the clean version feels too polite.
        </p>

        <div className="grid grid-cols-2 gap-3 mb-8" data-testid="tekki-variants">
          {VARIANTS.map((v) => (
            <Card key={v.key} className="border-border/40 overflow-hidden hover:border-primary/40 transition">
              <div
                className="flex items-center justify-center aspect-square"
                style={{ backgroundColor: '#0a0a0a' }}
                data-testid={`tekki-variant-preview-${v.key}`}
              >
                <img
                  src={`/downloads/t1-logo-${v.key}.png`}
                  alt={`T1 logo · ${v.label}`}
                  className="w-[88%] h-[88%] object-contain"
                  loading="lazy"
                />
              </div>
              <CardContent className="p-3">
                <p className="text-sm font-semibold">{v.label}</p>
                <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{v.sub}</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full mt-2 h-8 text-xs rounded-full"
                  onClick={() => triggerDownload(`t1-logo-${v.key}.png`)}
                  data-testid={`tekki-download-${v.key}`}
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  Download
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Why SVG — explainer */}
        <Card className="bg-card/60 border-border/30">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                <Maximize2 className="w-4 h-4 text-accent" />
              </div>
              <div>
                <p className="font-medium text-sm">Original scales infinitely</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Sleeve patch or shop-window banner — same file, no pixelation. SVG is a vector format, not a fixed-pixel image.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-accent/20 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-4 h-4 text-accent" />
              </div>
              <div>
                <p className="font-medium text-sm">Variants are texture-baked</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Funky versions are flat PNGs at 2000×2000 — perfect for prints up to A3, stickers, hoodies, social posts. Use the clean SVG above for large-format work (banners, vehicle wraps, billboards).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TekkiPage;
